import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem, FormControl,
    Chip, Paper, CircularProgress, Alert,
    Autocomplete, Stack
} from '@mui/material';
import * as api from './brandsApi';
import type {
    Brand, Company, BrandCategory, BarcodeType, SalesChannel,
    MainGroup, Location, TenantConfig
} from './brandsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, paperSx, FormRow, DetailViewShell } from '../common/detailView';
import { isSupportUser, type CurrentUser } from '../common/auth';

interface BrandViewProps {
    brandId: number | null;
    currentUser?: CurrentUser | null;
}

export function BrandView({ brandId, currentUser }: BrandViewProps) {
    const { t } = useTranslation();
    const detail = useDetailView<Brand>();

    const [companies, setCompanies] = useState<Company[]>([]);
    const [brandCategories, setBrandCategories] = useState<BrandCategory[]>([]);
    const [barcodeTypes, setBarcodeTypes] = useState<BarcodeType[]>([]);
    const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
    const [allMainGroups, setAllMainGroups] = useState<MainGroup[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
    const [brandConnectEnabled, setBrandConnectEnabled] = useState(false);

    const [selectedMainGroups, setSelectedMainGroups] = useState<MainGroup[]>([]);
    const [brandConnectSelectedLocations, setBrandConnectSelectedLocations] = useState<Location[]>([]);
    const [identifierDraft, setIdentifierDraft] = useState('');

    const isNew = brandId == null;

    const normalizeBrand = (b: Brand): Brand => {
        b.companies = b.companies ?? [];
        b.itemGroups = b.itemGroups ?? [];
        b.salesChannels = b.salesChannels ?? [];
        return b;
    };

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            const [companiesData, categoriesData, typesData, channelsData, mainGroupsData, locationsData, configData, bcEnabled] = await Promise.all([
                api.getCompanies(),
                api.getBrandCategories(),
                api.getBarcodeTypes(),
                api.getSalesChannels(),
                api.getMainGroups(),
                api.getLocations(),
                api.getTenantConfig(),
                api.isBrandConnectEnabled(),
            ]);

            setCompanies(companiesData);
            setBrandCategories(categoriesData);
            setBarcodeTypes(typesData);
            setSalesChannels(channelsData);
            setAllMainGroups(mainGroupsData);
            setLocations(locationsData);
            setTenantConfig(configData);
            setBrandConnectEnabled(bcEnabled);

            if (isNew) {
                const newBrand: Brand = {
                    id: 0,
                    brandCode: '',
                    brandDescription: '',
                    isActive: true,
                    useAlternativeArticleNumber: false,
                    useSupplierArticleNumber: false,
                    isGroupSaleBrand: false,
                    company: companiesData[0] ?? null,
                    companies: [],
                    brandCategory: null,
                    barcodeType: null,
                    salesChannels: [],
                    itemGroups: [],
                    brandConnectIdentifier: null,
                    brandConnect: null,
                };
                detail.setItem(newBrand);
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const brandData = await api.getBrand(brandId as number);
                if (!brandData) throw new Error(`Brand ${brandId} returned empty response`);
                detail.setItem(normalizeBrand(brandData));
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [brandId, isNew]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        const brand = detail.item;
        if (brand?.brandConnect?.status === 'Connected' && locations.length > 0) {
            const numbers = (brand.brandConnect.connectedLocations ?? [])
                .map(s => Number(s.split('-').pop()))
                .filter(n => !isNaN(n));
            setBrandConnectSelectedLocations(locations.filter(l => numbers.includes(l.locationNumber)));
        } else {
            setBrandConnectSelectedLocations([]);
        }
    }, [detail.item, locations]);

    useEffect(() => {
        setIdentifierDraft(detail.item?.brandConnectIdentifier ?? '');
    }, [detail.item]);

    const brand = detail.item;

    // Validation
    const isBrandCodeValid = (): boolean => {
        if (!tenantConfig || !brand?.brandCode) return false;
        const isAssigned = tenantConfig.assignedBrandCodes.includes(brand.brandCode);
        const isNumeric = !isNaN(parseFloat(brand.brandCode)) && isFinite(Number(brand.brandCode));
        return !isAssigned && isNumeric && brand.brandCode.length === tenantConfig.articleBrandCodeLength;
    };

    const isBrandCategoryValid = (): boolean => !!brand?.brandCategory && brand.brandCategory.id > 0;
    const isBarcodeTypeValid = (): boolean => !!brand?.barcodeType && brand.barcodeType.id > 0;
    const isBrandDescriptionValid = (): boolean => !!brand?.brandDescription && brand.brandDescription.length > 0;

    const isAllValid = (): boolean => {
        if (!detail.isNew) return isBrandDescriptionValid();
        return isBrandCodeValid() && isBrandCategoryValid() && isBarcodeTypeValid();
    };

    // Available main groups (not already on brand)
    const availableMainGroups = allMainGroups
        .filter(mg => !(brand?.itemGroups ?? []).some(ig => ig.mainGroup.id === mg.id))
        .sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode));

    // Actions
    const enableEditing = () => {
        detail.startEdit();
    };

    const cancelEditing = () => {
        detail.cancelEdit();
        setSelectedMainGroups([]);
    };

    const saveBrand = async () => {
        if (!brand) return;
        await detail.withSaving(async () => {
            // Ensure preferred company is in companies list
            if (brand.company && !brand.companies.some(c => c.id === brand.company!.id)) {
                brand.companies.push(brand.company);
            }

            if (detail.isNew) {
                const response = await api.createBrand(brand, selectedMainGroups, [], []);
                detail.setItem(normalizeBrand(response));
                detail.setIsNew(false);
                detail.setIsEditing(false);
                setSelectedMainGroups([]);
            } else {
                const response = await api.updateBrand(brand, selectedMainGroups, [], []);
                detail.setItem(normalizeBrand(response));
                detail.setIsEditing(false);
                setSelectedMainGroups([]);
            }
        });
    };

    const addMainGroups = async () => {
        if (selectedMainGroups.length === 0 || !brand) return;
        await detail.withSaving(async () => {
            await api.createItemGroups(selectedMainGroups, [brand]);
            const refreshed = await api.getBrand(brand.id);
            detail.setItem(normalizeBrand(refreshed));
            setSelectedMainGroups([]);
        });
    };

    const connectSupplierWithLocations = async (selected: Location[]) => {
        if (!brand) return;
        await detail.withSaving(async () => {
            const locationNumbers = selected.map(l => l.locationNumber);
            await api.connectBrandSupplier(brand.id, brand.brandConnect!.supplierTenant, locationNumbers);
            const refreshed = await api.getBrand(brand.id);
            detail.setItem(normalizeBrand(refreshed));
        });
    };

    const acceptInvite = () => connectSupplierWithLocations([]);

    const commitIdentifier = async () => {
        if (!brand) return;
        const trimmed = identifierDraft.trim();
        if (trimmed === (brand.brandConnectIdentifier ?? '')) return;
        await detail.withSaving(async () => {
            await api.setBrandConnectIdentifier(brand.id, trimmed);
            const refreshed = await api.getBrand(brand.id);
            detail.setItem(normalizeBrand(refreshed));
        });
    };

    const disconnectSupplier = async () => {
        if (!brand) return;
        await detail.withSaving(async () => {
            await api.disconnectBrandSupplier(brand.id);
            const refreshed = await api.getBrand(brand.id);
            detail.setItem(normalizeBrand(refreshed));
        });
    };

    const brandConnectSection = !detail.isNew && brandConnectEnabled && (brand?.brandConnect?.status || isSupportUser(currentUser)) ? (
        <Paper variant="outlined" sx={paperSx}>
            <Box p={2}>
                <Stack spacing={2}>
                    <Typography sx={sx13} fontWeight="bold">{t('brand.brandConnectSection')}</Typography>

                    {isSupportUser(currentUser) && (
                        <FormRow label={t('brand.brandConnectIdentifier')}>
                            <TextField
                                size="small"
                                fullWidth
                                value={identifierDraft}
                                onChange={e => setIdentifierDraft(e.target.value)}
                                onBlur={commitIdentifier}
                                disabled={detail.saving || brand?.brandConnect?.status === 'Connected'}
                            />
                        </FormRow>
                    )}

                    {brand?.brandConnect?.supplierName && (
                        <FormRow label={t('brand.brandConnectSupplier')}>
                            <Typography sx={sx13}>{brand.brandConnect.supplierName}</Typography>
                        </FormRow>
                    )}

                    {brand?.brandConnect?.status === 'Pending' && (
                        <Box>
                            <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                onClick={acceptInvite}
                                disabled={detail.saving}
                            >
                                {t('brand.brandConnectAccept')}
                            </Button>
                        </Box>
                    )}

                    {brand?.brandConnect?.status === 'Connected' && (
                        <Stack spacing={1}>
                            <FormRow label={t('brand.brandConnectLocations')}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={locations}
                                    getOptionLabel={o => `${o.locationNumber} ${o.locationName}`}
                                    value={brandConnectSelectedLocations}
                                    onChange={(_, value) => {
                                        setBrandConnectSelectedLocations(value);
                                        void connectSupplierWithLocations(value);
                                    }}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    disabled={detail.saving}
                                    renderInput={params => <TextField {...params} placeholder={t('brand.brandConnectSelectLocations')} />}
                                />
                            </FormRow>
                            <Box>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={disconnectSupplier}
                                    disabled={detail.saving}
                                    sx={{ color: '#fff' }}
                                >
                                    {t('brand.brandConnectDisconnect')}
                                </Button>
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Box>

            {detail.saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    ) : null;

    return (
        <Stack spacing={2}>
            <DetailViewShell<Brand>
                featureKey="devFeature_reactBrandView"
                title={`${brand?.brandCode ?? ''} ${brand?.brandDescription ?? ''}`}
                isEditing={detail.isEditing}
                isNew={detail.isNew}
                saving={detail.saving}
                canSave={isAllValid()}
                loading={detail.loading}
                error={detail.error}
                item={brand}
                onEdit={enableEditing}
                onSave={saveBrand}
                onCancel={cancelEditing}
                onClearError={() => detail.setError(null)}
            >
                {(item) => (<>
                    {/* Brand Code */}
                    <FormRow label={t('brand.brandcode')}>
                        {!detail.isNew ? (
                            <Typography sx={sx13}>{item.brandCode}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.brandCode}
                                onChange={e => detail.update('brandCode', e.target.value)}
                                error={detail.isEditing && !isBrandCodeValid()}
                            />
                        )}
                    </FormRow>

                    {/* Brand Description */}
                    <FormRow label={t('brand.branddescription')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.brandDescription}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.brandDescription}
                                onChange={e => detail.update('brandDescription', e.target.value)}
                                error={!isBrandDescriptionValid()}
                            />
                        )}
                    </FormRow>

                    {/* Preferred Company */}
                    <FormRow label={t('brand.preferredCompany')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.company?.companyName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.company?.id ?? ''}
                                    onChange={e => {
                                        const comp = companies.find(c => c.id === e.target.value);
                                        if (comp) detail.update('company', comp);
                                    }}
                                >
                                    {companies.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Companies */}
                    <FormRow label={t('brand.companies')}>
                        {!detail.isEditing ? (
                            <Box>
                                {item.companies
                                    .sort((a, b) => a.companyName.localeCompare(b.companyName))
                                    .map(c => <Typography sx={sx13} key={c.id}>{c.companyName}</Typography>)}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={companies}
                                getOptionLabel={o => o.companyName}
                                value={item.companies}
                                onChange={(_, value) => detail.update('companies', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('article.addCompany')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((c, i) => <Chip {...getTagProps({ index: i })} key={c.id} label={c.companyName} size="small" />)
                                }
                            />
                        )}
                    </FormRow>

                    {/* Brand Category */}
                    <FormRow label={t('brand.brandcategory')}>
                        {!detail.isNew ? (
                            <Typography sx={sx13}>
                                {item.brandCategory?.typeName}
                                {item.brandCategory && (
                                    item.brandCategory.isBarcodeGenerationAllowed
                                        ? ` (${t('brand.barcodegenerationallowed')})`
                                        : ` (${t('brand.barcodegenerationdisallowed')})`
                                )}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!isBrandCategoryValid()}>
                                <Select
                                    value={item.brandCategory?.id ?? ''}
                                    onChange={e => {
                                        const cat = brandCategories.find(c => c.id === e.target.value);
                                        if (cat) detail.update('brandCategory', cat);
                                    }}
                                >
                                    {brandCategories.map(c => (
                                        <MenuItem key={c.id} value={c.id}>{c.typeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Barcode Type */}
                    <FormRow label={t('article.barcodetype')}>
                        {!detail.isNew ? (
                            <Typography sx={sx13}>{item.barcodeType?.barcodeTypeName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!isBarcodeTypeValid()}>
                                <Select
                                    value={item.barcodeType?.id ?? ''}
                                    onChange={e => {
                                        const bt = barcodeTypes.find(b => b.id === e.target.value);
                                        if (bt) detail.update('barcodeType', bt);
                                    }}
                                >
                                    {barcodeTypes.map(b => (
                                        <MenuItem key={b.id} value={b.id}>{b.barcodeTypeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Sales Channels */}
                    <FormRow label={t('brand.salesChannels')}>
                        {!detail.isEditing ? (
                            <Box>
                                {item.salesChannels.map(sc => (
                                    <Typography sx={sx13} key={sc.id}>{sc.salesChannelCode} {sc.salesChannelDescription}</Typography>
                                ))}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={salesChannels}
                                getOptionLabel={o => `${o.salesChannelCode} ${o.salesChannelDescription}`}
                                value={item.salesChannels}
                                onChange={(_, value) => detail.update('salesChannels', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((sc, i) => (
                                        <Chip {...getTagProps({ index: i })} key={sc.id} label={`${sc.salesChannelCode} ${sc.salesChannelDescription}`} size="small" />
                                    ))
                                }
                            />
                        )}
                    </FormRow>

                    {/* Main Groups */}
                    {!detail.isNew && (
                        <>
                            <Box />
                            <FormRow label={`${t('brand.maingroups')}:`}>
                                <Stack spacing={1}>
                                    {detail.isEditing && (
                                        <Box display="flex" gap={1}>
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                fullWidth
                                                options={availableMainGroups}
                                                getOptionLabel={o => `${o.mainGroupCode} ${o.mainGroupDescription}`}
                                                value={selectedMainGroups}
                                                onChange={(_, value) => setSelectedMainGroups(value)}
                                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                                renderInput={params => <TextField {...params} placeholder={t('article.addMaingroup')} />}
                                            />
                                            {selectedMainGroups.length > 0 && (
                                                <Button variant="outlined" color="inherit" size="small" onClick={addMainGroups} disabled={detail.saving}>
                                                    +
                                                </Button>
                                            )}
                                        </Box>
                                    )}
                                    {item.itemGroups
                                        .sort((a, b) => a.mainGroup.mainGroupDescription.localeCompare(b.mainGroup.mainGroupDescription))
                                        .map(ig => (
                                            <Typography sx={sx13} key={ig.mainGroup.id}>
                                                {ig.mainGroup.mainGroupCode} {ig.mainGroup.mainGroupDescription}
                                            </Typography>
                                        ))}
                                </Stack>
                            </FormRow>
                        </>
                    )}
                </>)}
            </DetailViewShell>

            {brandConnectSection}
        </Stack>
    );
}
